/**
 * ComparisonHeader Component
 * Header with view mode toggle and comparison controls
 */

import { useComparisonStore } from '@/store'
import { ComparisonViewMode } from '@/types/comparison'
import { Table2, LayoutGrid, Layers, RefreshCw, AlertCircle } from 'lucide-react'
import clsx from 'clsx'

interface ComparisonHeaderProps {
  onRefresh?: () => void
}

export default function ComparisonHeader({ onRefresh }: ComparisonHeaderProps) {
  const {
    selectedAssetIds,
    comparisonMode,
    setComparisonMode,
    loading,
    usingMockData
  } = useComparisonStore()

  const viewModes: Array<{ id: ComparisonViewMode; label: string; icon: React.ReactNode }> = [
    { id: 'table', label: 'Table', icon: <Table2 size={16} /> },
    { id: 'side-by-side', label: 'Side by Side', icon: <LayoutGrid size={16} /> },
    { id: 'overlay', label: 'Overlay', icon: <Layers size={16} /> }
  ]

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
      {/* Left: Selection Info */}
      <div className="flex items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-surface-200">
            Asset Comparison
          </h2>
          <p className="text-sm text-surface-400">
            {selectedAssetIds.length > 0
              ? `Comparing ${selectedAssetIds.length} asset${selectedAssetIds.length !== 1 ? 's' : ''}`
              : 'Select assets to compare'}
          </p>
        </div>

        {/* Mock Data Warning */}
        {usingMockData && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-status-warning/10 border border-status-warning/30 rounded-lg">
            <AlertCircle size={14} className="text-status-warning" />
            <span className="text-xs text-status-warning">Using demo data</span>
          </div>
        )}
      </div>

      {/* Right: View Mode Toggle & Actions */}
      <div className="flex items-center gap-3">
        {/* View Mode Toggle */}
        <div className="flex items-center bg-surface-800 rounded-lg p-1">
          {viewModes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setComparisonMode(mode.id)}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors',
                comparisonMode === mode.id
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700'
              )}
              title={mode.label}
            >
              {mode.icon}
              <span className="hidden md:inline">{mode.label}</span>
            </button>
          ))}
        </div>

        {/* Refresh Button */}
        <button
          onClick={onRefresh}
          disabled={loading || selectedAssetIds.length === 0}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
            selectedAssetIds.length > 0
              ? 'bg-surface-700 hover:bg-surface-600 text-surface-200'
              : 'bg-surface-800 text-surface-500 cursor-not-allowed'
          )}
        >
          <RefreshCw
            size={14}
            className={loading ? 'animate-spin' : ''}
          />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>
    </div>
  )
}
